import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader } from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import ClientDialog from "../components/ClientDialog";
import ClientAttachments from "../components/ClientAttachments";
import { Save, Pencil, Phone, Mail, MapPin, Send, ArrowLeft, MessageSquarePlus, Building2, Loader2, Trash2 } from "lucide-react";
import { formatDate } from "../lib/format";

const ACTIVITY_TYPES = [
  { value: "call", label: "Call" },
  { value: "email", label: "Email" },
  { value: "visit", label: "Visit" },
  { value: "meeting", label: "Meeting" },
  { value: "quote", label: "Quote" },
  { value: "follow_up", label: "Follow-up" },
  { value: "project_discussion", label: "Project discussion" },
  { value: "memo", label: "Memo" },
  { value: "note", label: "General note" },
];

const STATUS_META = {
  lead: "Lead", potential: "Potential",
  quote_requested: "Quote Requested", quote_sent: "Quote Sent",
  waiting_customer: "Waiting on Customer", approved: "Approved",
  upcoming: "Upcoming", scheduled: "Scheduled", active: "In Progress",
  completed: "Completed", cancelled: "Cancelled",
};

function ActivityDialog({ open, onOpenChange, clientId, onSuccess }) {
  const [type, setType] = useState("call");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setType("call"); setNotes(""); } }, [open]);
  const submit = async (e) => {
    e.preventDefault();
    if (!notes.trim()) return toast.error("Add some notes for the activity");
    setBusy(true);
    try {
      await api.post(`/sales/clients/${clientId}/activities`, { activity_type: type, notes });
      toast.success("Activity logged");
      onOpenChange(false);
      onSuccess?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-md" data-testid="activity-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black tracking-tight">Log activity</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="activity-type-select" className="rounded-sm mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>{ACTIVITY_TYPES.map((t) => (<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>))}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Notes</Label>
            <Textarea rows={5} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="activity-notes-input" className="rounded-sm mt-1.5" placeholder="Called Dr. Doe — needs 40 chairs by March. Wants to see fabric samples first." />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="activity-submit-btn" className="bg-blue-800 hover:bg-blue-900 text-white rounded-sm">
              {busy && <Loader2 size={16} className="animate-spin mr-1.5" />} Log activity
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ConvertDialog({ open, onOpenChange, client, onSuccess }) {
  const [name, setName] = useState("");
  const [expectedInstall, setExpectedInstall] = useState("");
  const [expectedDelivery, setExpectedDelivery] = useState("");
  const [products, setProducts] = useState("");
  const [notes, setNotes] = useState("");
  const [estimatedValue, setEstimatedValue] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && client) {
      setName(`${client.company_name} — ${new Date().toLocaleString("en-US", { month: "short", year: "numeric" })}`);
      setExpectedInstall("");
      setExpectedDelivery("");
      setProducts(client.products_interested || "");
      setNotes("");
      setEstimatedValue(client.estimated_value ?? "");
    }
  }, [open, client]);

  if (!client) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/sales/clients/${client.id}/convert-to-project`, {
        name: name.trim(),
        status: "pending_approval",
        expected_install_date: expectedInstall || null,
        expected_delivery_date: expectedDelivery || null,
        products,
        notes,
        estimated_value: estimatedValue === "" ? null : Number(estimatedValue),
      });
      toast.success(`Submitted "${data.name}" for approval`);
      onOpenChange(false);
      onSuccess?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Submit failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-md max-w-2xl" data-testid="convert-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black tracking-tight">Submit for Approval</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="rounded-sm border border-amber-200 bg-amber-50/60 p-3 text-xs text-slate-700">
            The **Installation Manager** and **Owner** will be notified. Once they approve, this becomes an Upcoming Project on the calendar. Client info (company, contact, address, products, estimated value) will be carried over — no retyping.
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Project name</Label>
            <Input required value={name} onChange={(e) => setName(e.target.value)} data-testid="convert-name-input" className="rounded-sm mt-1.5" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Estimated value ($)</Label>
              <Input type="number" min={0} value={estimatedValue} onChange={(e) => setEstimatedValue(e.target.value)} data-testid="convert-value-input" className="rounded-sm mt-1.5" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Expected delivery</Label>
              <Input type="date" value={expectedDelivery} onChange={(e) => setExpectedDelivery(e.target.value)} data-testid="convert-delivery-input" className="rounded-sm mt-1.5" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Expected install</Label>
              <Input type="date" value={expectedInstall} onChange={(e) => setExpectedInstall(e.target.value)} data-testid="convert-install-input" className="rounded-sm mt-1.5" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Products involved</Label>
              <Input value={products} onChange={(e) => setProducts(e.target.value)} data-testid="convert-products-input" className="rounded-sm mt-1.5" />
            </div>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Project-specific notes for the installer</Label>
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="convert-notes-input" className="rounded-sm mt-1.5" placeholder="Anything the install team needs to know beyond the client memo." />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="convert-submit-btn" className="bg-blue-800 hover:bg-blue-900 text-white rounded-sm">
              {busy && <Loader2 size={16} className="animate-spin mr-1.5" />} Submit for Approval
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function ClientDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canDelete } = useAuth();
  const [client, setClient] = useState(null);
  const [memo, setMemo] = useState("");
  const [memoDirty, setMemoDirty] = useState(false);
  const [savingMemo, setSavingMemo] = useState(false);
  const [reps, setReps] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [{ data }, usersRes] = await Promise.all([
        api.get(`/sales/clients/${id}`),
        api.get("/users").catch(() => ({ data: [] })),
      ]);
      setClient(data);
      setMemo(data.memo || "");
      setMemoDirty(false);
      setReps((usersRes.data || []).filter((u) => u.role === "sales_representative"));
    } catch (err) {
      toast.error("Could not load client");
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const saveMemo = async () => {
    setSavingMemo(true);
    try {
      await api.patch(`/sales/clients/${id}`, { memo });
      toast.success("Memo saved");
      setMemoDirty(false);
      load();
    } catch (err) { toast.error("Save failed"); }
    finally { setSavingMemo(false); }
  };

  const doDelete = async () => {
    if (!window.confirm(`Delete client "${client.company_name}"? Linked projects will keep the reference, but the client record is gone.`)) return;
    try {
      await api.delete(`/sales/clients/${id}`);
      toast.success("Client removed");
      navigate("/sales");
    } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };

  if (!client) return <div className="p-8 text-slate-500 font-mono text-sm">Loading client…</div>;

  return (
    <div>
      <PageHeader
        eyebrow="Client"
        title={client.company_name}
        description={client.contact_name ? `Primary contact: ${client.contact_name}` : "No contact person set yet."}
        testId="client-header"
        actions={
          <div className="flex flex-wrap items-center gap-2 no-print">
            <Button variant="outline" onClick={() => navigate("/sales")} data-testid="back-to-sales-btn" className="rounded-sm border-slate-300">
              <ArrowLeft size={16} className="mr-1.5" /> Back
            </Button>
            <Button variant="outline" onClick={() => setActivityOpen(true)} data-testid="log-activity-btn" className="rounded-sm border-slate-300">
              <MessageSquarePlus size={16} className="mr-1.5" /> Log activity
            </Button>
            <Button variant="outline" onClick={() => setEditOpen(true)} data-testid="edit-client-btn" className="rounded-sm border-slate-300">
              <Pencil size={16} className="mr-1.5" /> Edit
            </Button>
            <Button onClick={() => setConvertOpen(true)} data-testid="convert-btn" className="bg-blue-800 hover:bg-blue-900 text-white rounded-sm">
              <Send size={16} className="mr-1.5" /> Submit for Approval
            </Button>
            {canDelete && (
              <Button variant="ghost" onClick={doDelete} data-testid="delete-client-btn" className="text-red-600 hover:bg-red-50 rounded-sm">
                <Trash2 size={16} />
              </Button>
            )}
          </div>
        }
      />

      <div className="px-4 md:px-8 pt-6 space-y-6">
        {/* Overview */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="border border-slate-200 rounded-md bg-white shadow-sm p-4 space-y-2" data-testid="client-info-card">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">Contact</div>
            {client.phone && <div className="text-sm text-slate-800 inline-flex items-center gap-2"><Phone size={14} className="text-slate-400" /> {client.phone}</div>}
            {client.email && <div className="text-sm text-slate-800 inline-flex items-center gap-2"><Mail size={14} className="text-slate-400" /> <a href={`mailto:${client.email}`} className="hover:text-blue-800">{client.email}</a></div>}
            {(client.city || client.state) && (
              <div className="text-sm text-slate-700 inline-flex items-center gap-2 pt-2 border-t border-slate-100">
                <MapPin size={14} className="text-slate-400" />
                <span>
                  {client.address && <>{client.address}, </>}{client.city}{client.state && `, ${client.state}`} {client.zip_code}
                  {client.county && <span className="text-slate-500"> · {client.county} Co.</span>}
                </span>
              </div>
            )}
          </div>
          <div className="border border-slate-200 rounded-md bg-white shadow-sm p-4 space-y-2" data-testid="client-status-card">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">Status</div>
            <div className="text-sm"><span className="text-slate-500">Priority:</span> <span className="font-semibold capitalize">{client.priority}</span></div>
            <div className="text-sm"><span className="text-slate-500">Client:</span> <span className="font-semibold capitalize">{client.client_status}</span></div>
            <div className="text-sm"><span className="text-slate-500">Lead:</span> <span className="font-semibold capitalize">{(client.lead_status || "").replace("_", " ")}</span></div>
            <div className="text-sm"><span className="text-slate-500">Customer type:</span> <span className="font-semibold capitalize">{client.customer_type}</span></div>
          </div>
          <div className="border border-slate-200 rounded-md bg-white shadow-sm p-4 space-y-2" data-testid="client-pipeline-card">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">Pipeline</div>
            <div className="text-sm"><span className="text-slate-500">Est. value:</span> <span className="font-mono font-bold text-slate-900">{client.estimated_value ? `$${Number(client.estimated_value).toLocaleString()}` : "—"}</span></div>
            <div className="text-sm"><span className="text-slate-500">Products:</span> <span className="text-slate-800">{client.products_interested || "—"}</span></div>
            <div className="text-sm"><span className="text-slate-500">Last contact:</span> <span className="font-mono text-xs">{client.last_contact_date || "—"}</span></div>
            <div className="text-sm"><span className="text-slate-500">Next follow-up:</span> <span className="font-mono text-xs">{client.next_followup_date || "—"}</span></div>
            {client.sales_rep && <div className="text-xs pt-2 border-t border-slate-100 mt-2 text-slate-500">Rep: <span className="font-semibold text-slate-800">{client.sales_rep.name}</span></div>}
          </div>
        </div>

        {/* Memo (Quill) */}
        <div className="border border-slate-200 rounded-md bg-white shadow-sm p-4" data-testid="client-memo-card">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Client memo</div>
            <Button size="sm" onClick={saveMemo} disabled={!memoDirty || savingMemo} data-testid="save-memo-btn" className="bg-blue-800 hover:bg-blue-900 text-white rounded-sm">
              {savingMemo ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Save size={14} className="mr-1.5" />}
              Save memo
            </Button>
          </div>
          <div data-testid="client-memo-editor">
            <ReactQuill
              theme="snow"
              value={memo}
              onChange={(v) => { setMemo(v); setMemoDirty(true); }}
              modules={{
                toolbar: [
                  [{ header: [1, 2, 3, false] }],
                  ["bold", "italic", "underline"],
                  [{ list: "ordered" }, { list: "bullet" }],
                  ["link"],
                  ["clean"],
                ],
              }}
              placeholder="Detailed notes about the client — needs, products discussed, past calls, special requests, preferences, concerns…"
            />
          </div>
        </div>

        {/* Linked projects */}
        <ClientAttachments clientId={id} />

        <div className="border border-slate-200 rounded-md bg-white shadow-sm" data-testid="client-projects-card">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 inline-flex items-center gap-2">
              <Building2 size={14} /> Linked projects
            </div>
            <span className="text-xs font-mono text-slate-500">{client.projects?.length || 0}</span>
          </div>
          {(!client.projects || client.projects.length === 0) ? (
            <div className="p-6 text-center text-sm text-slate-500">
              No projects yet. Use <strong>"Send to Upcoming Projects"</strong> above to spin one up.
            </div>
          ) : (
            <ul className="divide-y divide-slate-200">
              {client.projects.map((p) => (
                <li key={p.id} className="px-4 py-3 flex items-center justify-between gap-3">
                  <Link to={`/projects/${p.id}`} className="font-semibold text-slate-900 hover:text-blue-800 truncate">
                    {p.name}
                  </Link>
                  <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-sm bg-slate-100 text-slate-700 capitalize whitespace-nowrap">
                    {(STATUS_META[p.status] || p.status || "").toLowerCase()}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Activity log */}
        <div className="border border-slate-200 rounded-md bg-white shadow-sm" data-testid="client-activity-card">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Activity history</div>
            <Button size="sm" variant="outline" onClick={() => setActivityOpen(true)} className="rounded-sm">
              <MessageSquarePlus size={14} className="mr-1.5" /> Add
            </Button>
          </div>
          {(!client.activities || client.activities.length === 0) ? (
            <div className="p-6 text-center text-sm text-slate-500">No activity logged yet.</div>
          ) : (
            <ul className="divide-y divide-slate-200">
              {client.activities.map((a) => (
                <li key={a.id} className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-blue-100 text-blue-800 capitalize">
                      {(a.activity_type || "").replace("_", " ")}
                    </span>
                    <span className="text-xs font-mono text-slate-500">{formatDate(a.created_at)}</span>
                    <span className="text-xs text-slate-500">·</span>
                    <span className="text-xs text-slate-500">{a.user_name}</span>
                  </div>
                  {a.notes && <div className="mt-1 text-sm text-slate-800 whitespace-pre-wrap">{a.notes}</div>}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <ClientDialog open={editOpen} onOpenChange={setEditOpen} client={client} reps={reps} onSuccess={load} />
      <ActivityDialog open={activityOpen} onOpenChange={setActivityOpen} clientId={id} onSuccess={load} />
      <ConvertDialog open={convertOpen} onOpenChange={setConvertOpen} client={client} onSuccess={load} />
    </div>
  );
}
